/**
 * Currency formatting helpers. Wrap Intl.NumberFormat so the rest of the
 * app speaks in numbers and lets this module own the locale + currency
 * details.
 */

export type CurrencyOptions = {
  currency?: string;
  locale?: string;
  signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero';
  showCents?: boolean;
};

const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(opts: Required<Omit<CurrencyOptions, 'showCents'>> & {
  minimumFractionDigits: number;
  maximumFractionDigits: number;
}): Intl.NumberFormat {
  const key = `${opts.locale}|${opts.currency}|${opts.signDisplay}|${opts.minimumFractionDigits}|${opts.maximumFractionDigits}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(opts.locale, {
      style: 'currency',
      currency: opts.currency,
      signDisplay: opts.signDisplay,
      minimumFractionDigits: opts.minimumFractionDigits,
      maximumFractionDigits: opts.maximumFractionDigits,
    });
    formatterCache.set(key, f);
  }
  return f;
}

/**
 * Format a number as a currency string. Defaults: en-US, USD, 2 decimals.
 * Pass `showCents: false` to drop fraction digits when the value is whole.
 */
export function formatCurrency(
  amount: number,
  options: CurrencyOptions = {},
): string {
  const {
    currency = 'USD',
    locale = 'en-US',
    signDisplay = 'auto',
    showCents = true,
  } = options;
  const safe = Number.isFinite(amount) ? amount : 0;
  const min = showCents ? 2 : 0;
  const max = 2;
  return getFormatter({
    currency,
    locale,
    signDisplay,
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(safe);
}

/** Compact form like "$1.2k" / "$5.4M" — used in dense charts. */
export function formatCompactCurrency(
  amount: number,
  options: { currency?: string; locale?: string } = {},
): string {
  const { currency = 'USD', locale = 'en-US' } = options;
  const safe = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(safe);
}

/** Round to 2 decimals — avoids 0.1 + 0.2 === 0.30000000000000004. */
export function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}
